import type { HistoryPoint } from "@weather-deck/shared";
import { el } from "./dom";

type DayStats = {
  date: string;
  points: HistoryPoint[];
  deckAvg: number | null;
  dwdAvg: number | null;
};

export function buildHistoryTable(history: HistoryPoint[]): HTMLElement {
  const container = el("div", { className: "history-table-container" });

  // Group by day
  const daysMap = new Map<string, HistoryPoint[]>();
  for (const snap of history) {
    const d = new Date(snap.t);
    const dayStr = d.toLocaleDateString("en-US", { weekday: 'short', month: 'short', day: 'numeric' });
    if (!daysMap.has(dayStr)) daysMap.set(dayStr, []);
    daysMap.get(dayStr)!.push(snap);
  }

  const days: DayStats[] = [];
  for (const [date, snaps] of daysMap.entries()) {
    let deckSum = 0, deckCount = 0;
    let dwdSum = 0, dwdCount = 0;
    
    for (const s of snaps) {
      if (s.deck != null) { deckSum += s.deck; deckCount++; }
      if (s.dwd != null) { dwdSum += s.dwd; dwdCount++; }
    }

    days.push({
      date,
      points: snaps,
      deckAvg: deckCount > 0 ? deckSum / deckCount : null,
      dwdAvg: dwdCount > 0 ? dwdSum / dwdCount : null
    });
  }

  for (const day of days) {
    const details = el("details", { className: "history-day" });
    
    const deckAvgStr = day.deckAvg != null ? day.deckAvg.toFixed(1) + "°C" : "--";
    const dwdAvgStr = day.dwdAvg != null ? day.dwdAvg.toFixed(1) + "°C" : "--";
    let diffStr = "";
    if (day.deckAvg != null && day.dwdAvg != null) {
      const diff = day.deckAvg - day.dwdAvg;
      diffStr = ` (Deck ${diff > 0 ? '+' : ''}${diff.toFixed(1)}°C)`;
    }
    
    const summary = el("summary", { 
      text: `${day.date} — Deck: ${deckAvgStr} | DWD: ${dwdAvgStr}${diffStr}` 
    });
    details.appendChild(summary);
    
    const table = el("table", { className: "history-hourly" });
    const thead = el("thead");
    const trHead = el("tr");
    trHead.appendChild(el("th", { text: "Time" }));
    trHead.appendChild(el("th", { text: "Deck" }));
    trHead.appendChild(el("th", { text: "DWD" }));
    thead.appendChild(trHead);
    table.appendChild(thead);
    
    const tbody = el("tbody");
    for (const p of day.points) {
      const tr = el("tr");
      const timeStr = new Date(p.t).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });

      tr.appendChild(el("td", { text: timeStr }));
      tr.appendChild(el("td", { text: p.deck != null ? p.deck.toFixed(1) + "°C" : "--" }));
      tr.appendChild(el("td", { text: p.dwd != null ? p.dwd.toFixed(1) + "°C" : "--" }));
      tbody.appendChild(tr);
    }
    table.appendChild(tbody);
    details.appendChild(table);
    
    container.appendChild(details);
  }
  
  return container;
}
